/**
 * LUNJ: Keep Appstle selling plans in sync with Pack Size variant (and vice versa).
 * 14 DAY → 14 Servings (2 weeks) | 28 DAY → 28 Servings (4 weeks) | One-time → 14 Servings
 *
 * Radio UI is owned by Appstle — we never dispatch change events (that caused 14-day snap-back).
 */
(function () {
  const PACK_14 = '14 Servings (2 weeks)';
  const PACK_28 = '28 Servings (4 weeks)';

  const CARD_PRICES = {
    '14-day': { compare: '£36.99', price: '£33.29' },
    '28-day': { compare: '£73.98', price: '£62.88' },
    onetime: '£36.99',
  };

  let syncLock = false;
  let lastSubscriptionKind = null;
  let liquidPlanIds = null;

  function withSyncLock(fn) {
    if (syncLock) return;
    syncLock = true;
    try {
      fn();
    } finally {
      requestAnimationFrame(() => {
        syncLock = false;
      });
    }
  }

  function widgetRoot() {
    return document.querySelector('#appstle-widget-template');
  }

  function getLiquidPlanIds() {
    if (liquidPlanIds) return liquidPlanIds;
    const el = document.getElementById('lunj-selling-plan-ids');
    if (!el) return null;
    try {
      liquidPlanIds = JSON.parse(el.textContent);
    } catch {
      liquidPlanIds = null;
    }
    return liquidPlanIds;
  }

  function subscriptionWrappers() {
    const widget = widgetRoot();
    if (!widget) return [];
    return Array.from(
      widget.querySelectorAll(
        '.appstle_input_wrapper:not(.appstle_onetime_block)'
      )
    );
  }

  function wrapperLabelText(wrapper) {
    return (wrapper.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
  }

  function is14DayWrapper(wrapper) {
    const t = wrapperLabelText(wrapper);
    return /\b14\s*DAY\b/.test(t) && !/\b28\s*DAY/.test(t);
  }

  function is28DayWrapper(wrapper) {
    const t = wrapperLabelText(wrapper);
    return /\b28\s*DAY/.test(t);
  }

  function findSubscriptionWrapper(kind) {
    const wrappers = subscriptionWrappers();
    if (kind === '14-day') {
      return wrappers.find(is14DayWrapper) || wrappers[0] || null;
    }
    if (kind === '28-day') {
      return wrappers.find(is28DayWrapper) || wrappers[wrappers.length - 1] || null;
    }
    return null;
  }

  function getCheckedSubscriptionWrapper() {
    const widget = widgetRoot();
    if (!widget) return null;
    const radio = widget.querySelector(
      'input[name="selling_plan"]:not([value=""]):checked'
    );
    return radio?.closest('.appstle_input_wrapper') || null;
  }

  function subscriptionKindFromWrapper(wrapper) {
    if (!wrapper) return null;
    if (is28DayWrapper(wrapper)) return '28-day';
    if (is14DayWrapper(wrapper)) return '14-day';
    return null;
  }

  function getCheckedSubscriptionKind() {
    return subscriptionKindFromWrapper(getCheckedSubscriptionWrapper());
  }

  function isOneTimeSelected() {
    const oneTime = document.querySelector(
      '#appstle-widget-template input[name="selling_plan"][value=""]'
    );
    return oneTime && oneTime.checked;
  }

  function getActiveSubscriptionKind() {
    if (isOneTimeSelected()) return 'onetime';
    return getCheckedSubscriptionKind();
  }

  /** Set radio checked only — never dispatch change (Appstle fights back with 14-day). */
  function setSubscriptionRadioQuiet(kind) {
    if (!kind) return false;

    if (kind === 'onetime') {
      const oneTime = document.querySelector(
        '#appstle-widget-template input[name="selling_plan"][value=""]'
      );
      if (!oneTime) return false;
      if (!oneTime.checked) oneTime.checked = true;
      return true;
    }

    const wrapper = findSubscriptionWrapper(kind);
    const radio = wrapper?.querySelector('input[name="selling_plan"]');
    if (!radio) return false;
    if (!radio.checked) radio.checked = true;
    return true;
  }

  function getPackSizeValue() {
    const variantOptions = document.querySelector('variant-options');
    if (!variantOptions) return null;

    const packOption = variantOptions.querySelector(
      '.lunj-hide-pack-size input[type="radio"]:checked'
    );
    if (packOption) return packOption.value;

    const checked = variantOptions.querySelectorAll(
      'input[type="radio"]:checked'
    );
    for (const input of checked) {
      if (input.value && input.value.includes('Servings')) {
        return input.value;
      }
    }
    return null;
  }

  function packKindFromValue(value) {
    if (!value) return null;
    if (value.includes('28 Servings')) return '28-day';
    if (value.includes('14 Servings')) return '14-day';
    return null;
  }

  function getVariantOptionsEl() {
    return document.querySelector('variant-options');
  }

  function getVariantData() {
    const el = getVariantOptionsEl();
    if (!el) return [];
    const script = el.querySelector('script[type="application/json"]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  function getSelectedOptions() {
    const el = getVariantOptionsEl();
    if (!el) return [];
    return Array.from(el.querySelectorAll('.wt-product__option')).map(
      (fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value || ''
    );
  }

  function getPackSizeFieldsetIndex() {
    const el = getVariantOptionsEl();
    if (!el) return -1;
    return Array.from(el.querySelectorAll('.wt-product__option')).findIndex(
      (fs) => fs.classList.contains('lunj-hide-pack-size')
    );
  }

  function getPackSizeFieldset() {
    const el = getVariantOptionsEl();
    const idx = getPackSizeFieldsetIndex();
    if (!el || idx < 0) return null;
    return el.querySelectorAll('.wt-product__option')[idx] || null;
  }

  function isPackSizeHidden() {
    return getPackSizeFieldset()?.classList.contains('lunj-hide-pack-size') ?? false;
  }

  function rememberSubscriptionKind(kind) {
    if (kind === '14-day' || kind === '28-day' || kind === 'onetime') {
      lastSubscriptionKind = kind;
    }
  }

  function findVariantForPackSize(packValue) {
    const options = getSelectedOptions();
    const packIdx = getPackSizeFieldsetIndex();
    if (packIdx === -1) return null;
    const nextOptions = [...options];
    nextOptions[packIdx] = packValue;
    return (
      getVariantData().find(
        (variant) =>
          !variant.options
            .map((opt, i) => nextOptions[i] === opt)
            .includes(false)
      ) || null
    );
  }

  function setPackSizeQuiet(packValue) {
    const variantOptions = getVariantOptionsEl();
    if (!variantOptions) return false;

    const packIdx = getPackSizeFieldsetIndex();
    const fieldsets = Array.from(
      variantOptions.querySelectorAll('.wt-product__option')
    );
    const packFieldset = fieldsets[packIdx];
    if (!packFieldset) return false;

    const packInput = Array.from(
      packFieldset.querySelectorAll('input[type="radio"]')
    ).find((el) => el.value === packValue);
    if (!packInput) return false;

    const variant = findVariantForPackSize(packValue);
    if (!variant) return false;

    const sectionId = variantOptions.dataset.section;
    const formInput = document.querySelector(
      `#product-form-${sectionId} input[name="id"]`
    );
    if (
      packInput.checked &&
      formInput &&
      String(formInput.value) === String(variant.id)
    ) {
      return true;
    }

    packInput.checked = true;
    const label = packFieldset.querySelector('.wt-product__option__title .value');
    if (label) label.textContent = packValue;

    document
      .querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      )
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input) input.value = variant.id;
      });

    variantOptions.setAttribute('data-variant-id', variant.id);
    return true;
  }

  function variantForSubscriptionKind(kind) {
    if (kind === '28-day') return PACK_28;
    return PACK_14;
  }

  function kindFromSellingPlanRadio(radio) {
    if (!radio) return null;
    if (radio.value === '') return 'onetime';
    return subscriptionKindFromWrapper(
      radio.closest('.appstle_input_wrapper')
    );
  }

  function applyPackForSubscriptionKind(kind) {
    if (!kind || kind === 'onetime') {
      setPackSizeQuiet(PACK_14);
      return;
    }
    setPackSizeQuiet(variantForSubscriptionKind(kind));
  }

  /** Sync hidden pack + prices from a known kind (never touch Appstle radios). */
  function syncPackFromKind(kind) {
    if (!kind) return;
    applyPackForSubscriptionKind(kind);
    applyFixedPrices();
  }

  function sellingPlanIdForKind(kind) {
    if (!kind || kind === 'onetime') return null;

    const fromLiquid = getLiquidPlanIds();
    if (fromLiquid?.[kind]) {
      return String(fromLiquid[kind]);
    }

    const wrapper = findSubscriptionWrapper(kind);
    const radio = wrapper?.querySelector('input[name="selling_plan"]');
    const value = radio?.value;
    return value && value !== '' ? value : null;
  }

  function resolveSubscriptionKindForSubmit() {
    if (lastSubscriptionKind === 'onetime') return 'onetime';
    if (isOneTimeSelected()) return 'onetime';

    if (lastSubscriptionKind && lastSubscriptionKind !== 'onetime') {
      return lastSubscriptionKind;
    }

    const packKind = packKindFromValue(getPackSizeValue());
    if (packKind) return packKind;

    return getCheckedSubscriptionKind();
  }

  function ensureSellingPlanOnProductForm(kindOverride) {
    const variantOptions = getVariantOptionsEl();
    if (!variantOptions) return null;
    const form = document.getElementById(
      `product-form-${variantOptions.dataset.section}`
    );
    if (!form) return null;

    form.querySelectorAll('input[name="selling_plan"]').forEach((el) => el.remove());

    const kind = kindOverride || resolveSubscriptionKindForSubmit();
    if (!kind || kind === 'onetime') return null;

    const planId = sellingPlanIdForKind(kind);
    if (!planId) return null;

    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'selling_plan';
    hidden.value = planId;
    hidden.dataset.lunjSellingPlan = kind;
    form.appendChild(hidden);
    return planId;
  }

  function enforceMatchBeforeSubmit() {
    const subKind = resolveSubscriptionKindForSubmit();
    if (!subKind || subKind === 'onetime') {
      rememberSubscriptionKind('onetime');
      setPackSizeQuiet(PACK_14);
      ensureSellingPlanOnProductForm('onetime');
      return null;
    }

    rememberSubscriptionKind(subKind);
    setSubscriptionRadioQuiet(subKind);
    setPackSizeQuiet(variantForSubscriptionKind(subKind));
    return ensureSellingPlanOnProductForm(subKind);
  }

  window.lunjPrepareProductFormForCart = function (form) {
    if (!widgetRoot() || !form?.querySelector('input[name="id"]')) return;
    enforceMatchBeforeSubmit();
  };

  window.lunjPatchCartFormData = function (formData) {
    if (!widgetRoot() || !(formData instanceof FormData)) return;

    const subKind = resolveSubscriptionKindForSubmit();
    if (!subKind || subKind === 'onetime') {
      formData.delete('selling_plan');
      return;
    }

    const planId = sellingPlanIdForKind(subKind);
    if (!planId) return;

    formData.delete('selling_plan');
    formData.set('selling_plan', planId);
  };

  function installFetchGuard() {
    if (window.__lunjCartFetchGuard) return;
    window.__lunjCartFetchGuard = true;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof Request
            ? input.url
            : '';
      if (url.includes('/cart/add') && init?.body instanceof FormData) {
        window.lunjPatchCartFormData(init.body);
      }
      return nativeFetch(input, init);
    };
  }

  function applyFixedPrices() {
    subscriptionWrappers().forEach((wrapper) => {
      const kind = subscriptionKindFromWrapper(wrapper);
      if (!kind || !(kind in CARD_PRICES)) return;
      const prices = CARD_PRICES[kind];
      const compare = wrapper.querySelector(
        '.appstle_sellingPlan_formattedCompareAtPrice'
      );
      const price = wrapper.querySelector(
        '.appstle_sellingPlan_price.appstle_price_block:not(.appstle_sellingPlan_formattedCompareAtPrice)'
      );
      if (compare) compare.textContent = prices.compare;
      if (price) price.textContent = prices.price;
    });

    const onetime = document.querySelector(
      '#appstle-widget-template .appstle_onetime_block .appstle_onetime_amount'
    );
    if (onetime) onetime.textContent = CARD_PRICES.onetime;
  }

  function runInitialUpdate() {
    withSyncLock(() => {
      applyFixedPrices();
    });

    [100, 400, 800].forEach((ms) => {
      setTimeout(() => {
        withSyncLock(() => {
          applyFixedPrices();
        });
      }, ms);
    });
  }

  /**
   * If Appstle snaps back to 14-day after a click, re-apply the shopper's choice
   * without dispatching change (cart submit still uses lastSubscriptionKind + liquid IDs).
   */
  function correctRadioIfNeeded() {
    const intent = lastSubscriptionKind;
    if (!intent) return;

    const actual = getActiveSubscriptionKind();
    if (actual === intent) {
      syncPackFromKind(intent);
      return;
    }

    withSyncLock(() => {
      setSubscriptionRadioQuiet(intent);
      syncPackFromKind(intent);
    });
  }

  function bindSubscriptionHandlers() {
    document.addEventListener(
      'pointerdown',
      (e) => {
        const widget = widgetRoot();
        if (!widget?.contains(e.target)) return;

        const oneTime = e.target.closest?.('.appstle_onetime_block');
        if (oneTime) {
          rememberSubscriptionKind('onetime');
          return;
        }

        const wrapper = e.target.closest?.(
          '.appstle_input_wrapper:not(.appstle_onetime_block)'
        );
        if (!wrapper) return;

        const kind = subscriptionKindFromWrapper(wrapper);
        if (kind) rememberSubscriptionKind(kind);
      },
      true
    );

    /* Appstle owns selection — we only react after it fires change (bubble). */
    document.addEventListener(
      'change',
      (e) => {
        if (
          !e.target.matches?.(
            '#appstle-widget-template input[name="selling_plan"]'
          )
        ) {
          return;
        }
        if (!e.target.checked) return;

        const kind = kindFromSellingPlanRadio(e.target);
        if (!kind) return;

        rememberSubscriptionKind(kind);
        requestAnimationFrame(() => {
          withSyncLock(() => {
            syncPackFromKind(kind);
          });
        });
      },
      false
    );

    /* After click settles, fix snap-back to 14-day without fighting Appstle mid-click. */
    document.addEventListener(
      'click',
      (e) => {
        const widget = widgetRoot();
        if (!widget?.contains(e.target)) return;
        if (
          !e.target.closest?.(
            'input[name="selling_plan"], .appstle_input_wrapper, .appstle_onetime_block'
          )
        ) {
          return;
        }

        setTimeout(() => {
          correctRadioIfNeeded();
        }, 0);
        setTimeout(() => {
          correctRadioIfNeeded();
        }, 50);
      },
      false
    );
  }

  function init() {
    installFetchGuard();
    bindSubscriptionHandlers();

    function bindAddToCartGuard() {
      document.addEventListener(
        'pointerdown',
        (e) => {
          const btn = e.target.closest?.(
            'product-form [type="submit"], .product-form__submit, .js-add-to-cart'
          );
          if (!btn || !widgetRoot()) return;
          const form = btn.closest('form');
          if (!form?.querySelector('input[name="id"]')) return;
          withSyncLock(() => {
            enforceMatchBeforeSubmit();
          });
        },
        true
      );

      document.addEventListener(
        'submit',
        (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (!form.querySelector('input[name="id"]')) return;
          if (!widgetRoot()) return;
          withSyncLock(() => {
            enforceMatchBeforeSubmit();
          });
        },
        true
      );
    }

    bindAddToCartGuard();

    function tryRun() {
      const widget = widgetRoot();
      if (widget && widget.querySelector('.appstle-cancel-text')) {
        runInitialUpdate();
        return true;
      }
      return false;
    }

    function startObserving() {
      if (tryRun()) return;
      const observer = new MutationObserver(() => {
        if (tryRun()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    if (document.body) {
      startObserving();
    } else {
      document.addEventListener('DOMContentLoaded', startObserving);
    }
  }

  init();
})();
